import React, { useState, useMemo } from 'react';
import { Box, Typography, CircularProgress, Checkbox, TextField, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';
import { RiskPoolPolicySolution } from '../../../../_types/solution';
import { translate as t } from 'hooks/useTranslations';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams } from '@mui/x-data-grid-pro';

interface EditProps {
  solutions: RiskPoolPolicySolution[];
  onSolutionsChange: (solutions: RiskPoolPolicySolution[]) => void;
  isLoading: boolean;
}

const Edit: React.FC<EditProps> = ({ solutions, onSolutionsChange, isLoading }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSolutions = useMemo(() => {
    if (!searchQuery) return solutions;
    const lowerQuery = searchQuery.toLowerCase();
    return solutions.filter(
      s =>
        s.manualName.toLowerCase().includes(lowerQuery) ||
        s.orgName.toLowerCase().includes(lowerQuery)
    );
  }, [solutions, searchQuery]);

  const handleToggle = (manualId: string) => {
    const updatedSolutions = solutions.map(solution => {
      if (solution.manualId === manualId) {
        return {
          ...solution,
          state: solution.state === 'ENABLED' ? 'DISABLED' : 'ENABLED'
        } as RiskPoolPolicySolution;
      }
      return solution;
    });
    onSolutionsChange(updatedSolutions);
  };

  const columns: ColumnDef = useMemo(() => ({
    manualName: {
      header: t('manageSolutions.riskPool.policy.manual'),
      flex: 0.6,
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolPolicySolution;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              edge="start"
              checked={row.state === 'ENABLED'}
              onChange={() => handleToggle(row.manualId)}
              sx={{ p: 0, pl: 2 }}
            />
            {row.manualName}
          </Box>
        );
      }
    },
    orgName: {
      header: t('manageSolutions.riskPool.policy.organization'),
      flex: 0.4,
    },
  }), [solutions]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (solutions.length === 0 && !isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="bodyMd" color="text.secondary">
          {t('manageSolutions.riskPool.policy.noManualsAvailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Input */}
      <Box sx={{ p: 0, pb: 5, width: '50%' }}>
        <TextField
          sx={{ pt: 0 }}
          fullWidth
          placeholder={t('manageSolutions.riskPool.policy.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      {/* Grid */}
      <Box sx={{ height: 400, width: '100%' }}>
        {filteredSolutions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="bodyMd" color="text.secondary">
              {t('manageSolutions.riskPool.policy.noResults')}
            </Typography>
          </Box>
        ) : (
          <GridSection
            columns={columns}
            data={filteredSolutions}
            isLoading={isLoading}
            gridKey="risk-pool-edit-grid"
            totalCount={solutions.length}
            gridProps={{
              getRowId: (row: RiskPoolPolicySolution) => row.manualId,
              rowHeight: 48
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default Edit;
